import { Injectable, Injector } from "@angular/core";
import { Observable, tap } from "rxjs";
import { SupportModel } from "src/app/core/models/global/clients/support.model";
import { ServiceBase } from "../../service-base";
import { RequestAccessModel } from "src/app/core/models/global/clients/request-access.model";
import { SupportDashboardModel } from "src/app/core/models/global/support/support-dashboard.model";
import { SupportDashboardDetailRequestModel } from "src/app/core/models/global/support/support-dashboard-detail-request.model";
import { SupportTimeSeriesTablesModel } from "src/app/core/models/global/support/support-asset-timeseries-tables.model";
import { SupportEmailSummaryDetailsModel } from "src/app/core/models/global/support/support-email-summary-details.model";
import { SupportEmailAutoDetailsModel } from "src/app/core/models/global/support/support-email-auto-details.model";
import { SupportOdinDetailsModel } from "src/app/core/models/global/support/support-database-odin.model";

@Injectable({
    providedIn: 'root'
})
export class SupportService extends ServiceBase {

    constructor(injector: Injector) {
        super(injector);
    }

    /**
     * Creates the support request.
     * @param supportModel The support details.
     * @returns Observable with the new tenant.
     */
    public submitSupportRequest(supportModel: SupportModel): Observable<SupportModel> {
        let formData = new FormData();
        formData.append('subject', supportModel.subject ?? '');
        formData.append('supportType', supportModel.supportType ?? '');
        formData.append('message', supportModel.message ?? '');

        if (supportModel.attachments) {
			for (var i = 0; i < supportModel.attachments.length; i++) {
				formData.append('attachments', supportModel.attachments[i]);
			}
		}

        return this.httpQueue.post<SupportModel>(`${this.baseUrl}v1/Support/SubmitSupportRequest`, formData);
    }

    /**
     * Creates the access request.
     * @param supportModel The support details.
     * @returns Observable with the new tenant.
     */
    public submitAccessRequest(accessRequest: RequestAccessModel): Observable<RequestAccessModel> {
        return this.httpQueue.post<RequestAccessModel>(`${this.baseUrl}v1/Support/SubmitAccessRequest`, accessRequest);
    }

    /**
     * Gets the data for the support dashboard.
     * @returns The support dashboard data as an observable.
     */
    public getSupportDashboardData(): Observable<SupportDashboardModel> {
        return this.httpQueue.get<SupportDashboardModel>(`${this.baseUrl}v1/Support/GetSupportDashboard`);
    }

    /**
     * Gets the timeseries tables details for the selected asset and scale.
     * @param tenantId The ID of the tenant.
     * @param assetId The ID of the asset.
     * @param databaseType The type of the database.
     * @param scale The scale of the timeseries data.
     * @returns An observable with the timeseries tables details.
     */
    public getSupportTimeSeriesTables(tenantId: string, assetId: string, databaseType: string, scale: string): Observable<SupportTimeSeriesTablesModel[]> {
        let model = new SupportDashboardDetailRequestModel();
        model.tenantId = tenantId;
        model.assetId = assetId;
        model.databaseType = databaseType;
        model.scale = scale;
        // Make the API call to get the timeseries tables details for the selected asset and scale
        return this.httpQueue.post<SupportTimeSeriesTablesModel[]>(`${this.baseUrl}v1/Support/GetAssetTimeSeriesTableLatestTimeDetails`, model).pipe(
            tap(data => {
                data.map((item) => {
                    if (item.maxDateCalc)
                        item.maxDateCalc = new Date(item.maxDateCalc);
                });
            })
        );
    }

    /**
     * Gets data for Email Summary Count chart
     * @returns Observable with the chart data
     */
    public getEmailSummaryCountChartData(tenantId: string, assetId: string, pageName: string, chartName: string): Observable<SupportEmailSummaryDetailsModel> {
        let model = new SupportDashboardDetailRequestModel();
        model.tenantId = tenantId;
        model.assetId = assetId;
        model.pageName = pageName;
        model.chartName = chartName;
        // Make the API call to get the email summary details for the selected asset
        return this.httpQueue.post<SupportEmailSummaryDetailsModel>(`${this.baseUrl}v1/Support/GetEmailSummaryCountChartData`, model).pipe(
            tap(data => {
                data.tableData.map((item) => {
                    item.timeSent = new Date(item.timeSent);
                });
            })
        );
    }

    /**
     * Gets data for Email Auto Count chart
     * @returns Observable with the chart data
     */
    public getEmailAutoCountChartData(tenantId: string, assetId: string, assetName: string, pageName: string, chartName: string): Observable<SupportEmailAutoDetailsModel> {
        let model = new SupportDashboardDetailRequestModel();
        model.tenantId = tenantId;
        model.assetId = assetId;
        model.assetName = assetName;
        model.pageName = pageName;
        model.chartName = chartName;
        // Make the API call to get the email auto details for the selected asset
        return this.httpQueue.post<SupportEmailAutoDetailsModel>(`${this.baseUrl}v1/Support/GetEmailAutoCountChartData`, model).pipe(
            tap(data => {
                data.tableData.map((item) => {
                    item.scheduledTime = new Date(item.scheduledTime);
                    if (item.sentTime)
                        item.sentTime = new Date(item.sentTime);
                });
            })
        );
    }

    /**
     * Gets data for Database Odin Details chart and table
     * @returns Observable with the chart data
     */
    public getDatabaseOdinDetailsData(tenantId: string, assetId: string, assetName: string, pageName: string, chartName: string): Observable<SupportOdinDetailsModel> {
        let model = new SupportDashboardDetailRequestModel();
        model.tenantId = tenantId;
        model.assetId = assetId;
        model.assetName = assetName;
        model.pageName = pageName;
        model.chartName = chartName;
        // Make the API call to get the database odin details for the selected asset
        return this.httpQueue.post<SupportOdinDetailsModel>(`${this.baseUrl}v1/Support/GetOdinDetailData`, model).pipe(
            tap(data => {
                data.tableData.map((item) => {
                    item.dateCalc = new Date(item.dateCalc);
                    item.startTime = new Date(item.startTime);
                    item.endTime = new Date(item.endTime);
                });
            })
        );
    }
}